import { AvField, AvForm } from "availity-reactstrap-validation";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
    Table,
    UncontrolledAlert,
    Collapse
} from "reactstrap";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DATABASE_VEHICULE_PAGE } from "../../routes/routeConstants";
import { vehiculeActions } from "./vehiculeSlice";
import CurrencyInput from "react-currency-input-field";
import { dataActions } from "../dataSlice";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { deleteDocument, extractData, uploadDocument as uploadVehicleDocument } from "./vehiculeService";
import { isVehiclePhoto, resolveDocumentUrl } from "./documentUrl";

const initForm = {
    id: 0,
    categVeh: '',
    reform: 'TECHNIQUE',
    marque: '',
    type: '',
    genre: '',
    modele: '',
    carburant: '',
    numChassis: '',
    nbrCylindres: 0,
    puissFiscale: 0,
    immatriculation: "",
    immanterieur: "",
    firstMec: null,
    mecMaroc: null,
    dateSinistre: null,
    ageMonths: 0,
    ageYears: 0,
    etatVehicule: null,
    usage: "",
    proprietere: "",
    prestataire: null,
    compteur: 0,
    fraisimm: 0,
    ville: null,
    assurance: null,
    entretReg: null,
    valMarche: 0,
    epave: 0,
    devis: 0,
    prixAchat: 0,
    coefCommercial: 0,
    documents: [],
};

const documentLabelKeys = {
    CG: "vehiculef.cgFull",
    CG1: "vehiculef.cg",
    CG2: "vehiculef.cg2",
    FACT: "vehiculef.facture",
    CIN: "vehiculef.cin",
    PHOTO: "vehiculef.photos",
};

const extractedFieldLabelKeys = {
    marque: "vehiculef.marque",
    type: "vehiculef.type",
    genre: "vehiculef.genre",
    modele: "vehiculef.modele",
    carburant: "vehiculef.carburant",
    numChassis: "vehiculef.numChassis",
    nbrCylindres: "vehiculef.nbrCylindres",
    puissFiscale: "vehiculef.puissFiscale",
    immatriculation: "vehiculef.immatriculation",
    immanterieur: "vehiculef.immanterieur",
    firstMec: "vehiculef.firstMec",
    mecMaroc: "vehiculef.mecMaroc",
    usage: "vehiculef.usage",
    proprietere: "vehiculef.proprietere",
    compteur: "vehiculef.compteur",
    prixAchat: "vehiculef.prixAchat",
    remise: "vehiculef.remise",
    fraisimm: "vehiculef.fraisimm",
};

const extractedFieldsByDocument = {
    CG: ["immatriculation", "immanterieur", "firstMec", "mecMaroc", "usage", "proprietere", "marque", "type", "genre", "modele", "carburant", "numChassis", "nbrCylindres", "puissFiscale"],
    CG1: ["immatriculation", "immanterieur", "firstMec", "mecMaroc", "usage", "proprietere"],
    CG2: ["marque", "type", "genre", "modele", "carburant", "numChassis", "nbrCylindres", "puissFiscale"],
    FACT: ["marque", "type", "genre", "modele", "numChassis", "immatriculation", "firstMec", "puissFiscale", "compteur", "prixAchat", "remise", "fraisimm"],
    CIN: ["proprietere", "immatriculation", "immanterieur", "firstMec"],
};

const dateFields = new Set(["firstMec", "mecMaroc", "dateSinistre"]);

const formSteps = [
    { key: "carteGrise", icon: "ri-file-copy-2-line", labelKey: "vehiculef.section_carte_grise" },
    { key: "facture", icon: "ri-bill-line", labelKey: "vehiculef.section_facture" },
    { key: "details", icon: "ri-car-line", labelKey: "vehiculef.section_details" },
    { key: "photos", icon: "ri-image-add-line", labelKey: "vehiculef.section_photos" },
    { key: "resume", icon: "ri-clipboard-line", labelKey: "vehiculef.section_resume" },
];

const documentTypesByStep = {
    carteGrise: ["CG", "CG1", "CG2"],
    facture: ["FACT"],
    photos: ["PHOTO"],
};

const VehiculeForm = () => {
    const { t } = useTranslation('translation');
    const [formState, setForm] = useState(initForm);
    const { isFetching } = useSelector(state => state.vehicule);
    const { categVH, reform, villes, assurances, entrestruc } = useSelector(state => state.data);
    const [cgFull, setCgFull] = useState(null);
    const [cg, setCg] = useState(null);
    const [cg2, setCg2] = useState(null);
    const [facture, setFacture] = useState(null);
    const [error, setError] = useState(null);
    const [recordError, setRecordError] = useState({ open: false, message: null });
    const [saveConfirm, setSaveConfirm] = useState(false);
    const [extractingDocument, setExtractingDocument] = useState(null);
    const [extractedDocuments, setExtractedDocuments] = useState({});
    const isParsingDocument = Boolean(extractingDocument);
    const cgFullInputRef = useRef(null);
    const cgInputRef = useRef(null);
    const cg2InputRef = useRef(null);
    const factureInputRef = useRef(null);
    const photosInputRef = useRef(null);
    const [photos, setPhotos] = useState([]);
    const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], index: 0 });
    const [deletingDocumentId, setDeletingDocumentId] = useState(null);

    const [currentStep, setCurrentStep] = useState("carteGrise");

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const selectedPhotoPreviews = useMemo(() => (
        photos.map((photo, index) => ({
            id: `selected-${photo.name}-${index}`,
            url: URL.createObjectURL(photo),
            title: photo.name,
            source: "selected",
            selectedIndex: index,
        }))
    ), [photos]);

    useEffect(() => () => {
        selectedPhotoPreviews.forEach((photo) => URL.revokeObjectURL(photo.url));
    }, [selectedPhotoPreviews]);

    const existingPhotoPreviews = useMemo(() => (
        (formState.documents || [])
            .filter(isVehiclePhoto)
            .map((doc) => ({
                id: doc.id || doc.filename || doc.path,
                url: resolveDocumentUrl(doc),
                title: doc.filenameUser || doc.filename || t("vehiculef.photos"),
                source: "saved",
                document: doc,
            }))
    ), [formState.documents, t]);

    const allPhotoPreviews = useMemo(() => (
        [...existingPhotoPreviews, ...selectedPhotoPreviews]
    ), [existingPhotoPreviews, selectedPhotoPreviews]);

    const currentStepDocuments = useMemo(() => {
        const allowedTypes = documentTypesByStep[currentStep] || [];
        return (formState.documents || []).filter(doc => allowedTypes.includes(doc?.type));
    }, [currentStep, formState.documents]);

    const currentStepFileDocuments = useMemo(() => (
        currentStepDocuments.filter(doc => doc?.type !== "PHOTO")
    ), [currentStepDocuments]);

    const resumeDocuments = useMemo(() => {
        const savedDocuments = (formState.documents || []).map(doc => ({
            id: `saved-${doc.id || doc.filename}`,
            label: t(documentLabelKeys[doc.type] || doc.type || "Document"),
            name: doc.filenameUser || doc.filename || "-",
            source: "saved",
            document: doc,
            open: () => window.open(resolveDocumentUrl(doc), "_blank"),
        }));

        const selectedDocuments = [
            { type: "CG", file: cgFull },
            { type: "CG1", file: !cgFull ? cg : null },
            { type: "CG2", file: !cgFull ? cg2 : null },
            { type: "FACT", file: facture },
        ].filter(item => item.file).map(({ type, file }) => ({
            id: `selected-${type}-${file.name}`,
            label: t(documentLabelKeys[type] || type),
            name: file.name,
            source: "selected",
            open: () => window.open(URL.createObjectURL(file), "_blank"),
        }));

        return [...savedDocuments, ...selectedDocuments];
    }, [formState.documents, cgFull, cg, cg2, facture, t]);

    const openPhotoViewer = (photoList, index = 0) => {
        if (!photoList.length) return;
        setPhotoViewer({ open: true, photos: photoList, index });
    };

    useEffect(() => {
        if (categVH.length == 0) {
            dispatch(dataActions.loadData())
        }
    }, [])

    useEffect(() => {
        if (id) {
            let payload = {
                id,
                onSuccess: (data) => {
                    setForm({ ...data });
                }
            }
            dispatch(vehiculeActions.find(payload));
        }
    }, [id, dispatch]);

    const currentStepIndex = formSteps.findIndex(step => step.key === currentStep);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === formSteps.length - 1;

    const goToStep = (stepKey) => {
        setCurrentStep(stepKey);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goToNextStep = () => {
        if (!isLastStep) {
            goToStep(formSteps[currentStepIndex + 1].key);
        }
    };

    const goToPreviousStep = () => {
        if (!isFirstStep) {
            goToStep(formSteps[currentStepIndex - 1].key);
        }
    };

    const formik = useFormik({
        initialValues: { ...formState },
        enableReinitialize: true,
        onSubmit: (values) => {

            setError(null);

            const normalizedValues = normalizePayloadDates(values);
            const isUpdate = values.id > 0;

            const payload = {
                data: normalizedValues,
                onSuccess: async (vehicle) => {
                    const savedVehicle = getApiBody(vehicle);
                    const vId = isUpdate ? values.id : savedVehicle?.id;

                    if (!vId) {
                        setRecordError({
                            open: true,
                            message: "Impossible d'associer les photos: identifiant vehicule introuvable.",
                        });
                        return;
                    }

	                    try {
                            const documentUploads = [
                                ...(cgFull ? [{ file: cgFull, type: "CG" }] : [
                                    ...(cg ? [{ file: cg, type: "CG1" }] : []),
                                    ...(cg2 ? [{ file: cg2, type: "CG2" }] : []),
                                ]),
                                ...(facture ? [{ file: facture, type: "FACT" }] : []),
                                ...photos.map(photo => ({ file: photo, type: "PHOTO" })),
                            ];

	                        await Promise.all(documentUploads.map(({ file, type }) => {
	                            const formData = new FormData();
	                            formData.append("file", file);
	                            formData.append("vId", vId);
	                            formData.append("documentType", type);
	
	                            return uploadVehicleDocument(formData);
	                        }));
	                    } catch (e) {
	                        setRecordError({
	                            open: true,
	                            message: e?.response?.data?.message || "Erreur lors de l'enregistrement des documents.",
	                        });
	                        return;
	                    }

                    navigate(DATABASE_VEHICULE_PAGE);
                }
            };

            // Create or update
            if (isUpdate) {
                dispatch(vehiculeActions.update(payload));
            } else {
                dispatch(vehiculeActions.create(payload));
            }
        }
    });

    const requestSave = () => {
        setSaveConfirm(true);
    };

    const confirmSave = () => {
        setSaveConfirm(false);
        formik.handleSubmit();
    };


    const handleChangeAssurance = ({ target }) => {
        formik.setFieldValue("assurance", { id: target.value });
    }

    const handleChangeVille = ({ target }) => {
        formik.setFieldValue("ville", { id: target.value });
    }

    const formatDateAsUserTypes = (value) => {
        const rawValue = String(value || "");
        if (rawValue.includes("/")) {
            const [rawDay = "", rawMonth = "", rawYear = ""] = rawValue.split("/");
            const day = rawDay.replace(/\D/g, "").slice(0, 2);
            const month = rawMonth.replace(/\D/g, "").slice(0, 2);
            const year = rawYear.replace(/\D/g, "").slice(0, 4);

            if (rawYear || rawValue.endsWith("//")) return `${day}/${month}/${year}`;
            if (month.length === 2 && !rawValue.endsWith("/")) return `${day}/${month}/`;
            if (rawValue.endsWith("/") && month) return `${day}/${month}/`;
            if (rawMonth || rawValue.endsWith("/")) return `${day}/${month}`;
            return day;
        }

        const digits = rawValue.replace(/\D/g, "").slice(0, 8);
        if (digits.length === 1 && Number(digits) > 3) return `${digits}/`;
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        if (digits.length === 7) return `${digits.slice(0, 1)}/${digits.slice(1, 3)}/${digits.slice(3)}`;
        return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    };

    const formatDateForDisplay = (value) => {
        if (!value) return "";

        const dateValue = String(value);
        const isoMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            return `${Number(isoMatch[3])}/${isoMatch[2]}/${isoMatch[1]}`;
        }

        const slashMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{1,4})$/);
        if (slashMatch) {
            return `${Number(slashMatch[1])}/${slashMatch[2].padStart(2, "0")}/${slashMatch[3]}`;
        }

        return formatDateAsUserTypes(dateValue);
    };

    const parseFormattedDate = (value) => {
        if (!value) return null;

        const dateValue = String(value);
        const isoDate = new Date(dateValue);
        if (/^\d{4}-\d{2}-\d{2}/.test(dateValue) && !isNaN(isoDate.getTime())) {
            return isoDate;
        }

        const match = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return new Date(dateValue);

        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);
        return new Date(year, month - 1, day);
    };

    const toIsoDateString = (value) => {
        if (!value) return null;

        const dateValue = String(value);
        const isoMatch = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) return isoMatch[1];

        const match = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return value;

        const day = match[1].padStart(2, "0");
        const month = match[2].padStart(2, "0");
        return `${match[3]}-${month}-${day}`;
    };

    const normalizePayloadDates = (values) => ({
        ...values,
        categVeh: values.categVeh || null,
        firstMec: toIsoDateString(values.firstMec),
        mecMaroc: toIsoDateString(values.mecMaroc),
        dateSinistre: toIsoDateString(values.dateSinistre),
    });

    const normalizeDateFieldValue = (field, value) => {
        if (!dateFields.has(field)) return value;
        return formatDateForDisplay(value);
    };

    const handleDateFieldChange = (field, value) => {
        formik.setFieldValue(field, formatDateAsUserTypes(value), false);
    };

    useEffect(() => {
        const { firstMec, dateSinistre } = formik.values;

        if (firstMec) {
            const age = calculateAge(firstMec, dateSinistre);
            formik.setFieldValue("ageYears", age.years, false);
            formik.setFieldValue("ageMonths", age.months, false);
        }
    }, [formik.values.firstMec, formik.values.dateSinistre]);

    const getApiBody = (response) => response?.data?.body ?? response?.body ?? response?.data ?? response;

    const clearDocumentFile = (documentType) => {
        const inputRefs = {
            CG: cgFullInputRef,
            CG1: cgInputRef,
            CG2: cg2InputRef,
            FACT: factureInputRef,
            PHOTO: photosInputRef,
        };

        if (documentType === "CG") {
            setCgFull(null);
        } else if (documentType === "CG1") {
            setCg(null);
        } else if (documentType === "CG2") {
            setCg2(null);
        } else if (documentType === "FACT") {
            setFacture(null);
        } else if (documentType === "PHOTO") {
            setPhotos([]);
        }

        if (inputRefs[documentType]?.current) {
            inputRefs[documentType].current.value = "";
        }

    };

    const getExtractedFields = (data, documentType) => {
        if (!data) return [];

        const configuredFields = extractedFieldsByDocument[documentType] || Object.keys(extractedFieldLabelKeys);
        return configuredFields
            .filter((field) => Object.prototype.hasOwnProperty.call(data, field))
            .map((field) => ({ field, label: t(extractedFieldLabelKeys[field] || field), value: data[field] ?? "" }));
    };

    const updateExtractedField = (documentType, field, value) => {
        const nextValue = normalizeDateFieldValue(field, value);
        setExtractedDocuments(prev => ({
            ...prev,
            [documentType]: {
                ...(prev[documentType] || {}),
                [field]: nextValue,
            },
        }));
    };

    const applyExtractedData = (data, documentType, advanceAfterApply = true) => {
        if (!data) return;

        getExtractedFields(data, documentType).forEach(({ field, value }) => {
            if (value !== undefined && value !== null && value !== "") {
                formik.setFieldValue(field, normalizeDateFieldValue(field, value), false);
            }
        });

        if (!advanceAfterApply) return;

        if (documentType === "CG" || documentType === "CG2") {
            goToStep("facture");
        } else if (documentType === "FACT") {
            goToStep("details");
        }
    };

    const extractVehicleDocument = async (file, documentType) => {
        if (isParsingDocument) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("documentType", documentType);

        setExtractingDocument(documentType);
        try {
            const response = await extractData(formData);
            const extracted = getApiBody(response);
            setExtractedDocuments(prev => ({ ...prev, [documentType]: extracted }));
            applyExtractedData(extracted, documentType, true);
        } catch (e) {
            setRecordError({
                open: true,
                message: e?.response?.data?.message || t("vehiculef.extractError"),
            });
        } finally {
            setExtractingDocument(null);
        }
    };

    const setDocumentFile = async (file, documentType) => {
        if (!file) return;

        if (documentType === "FACT") {
            setFacture(file);
        } else if (documentType === "CG") {
            setCgFull(file);
        } else if (documentType === "CG1") {
            setCg(file);
        } else if (documentType === "CG2") {
            setCg2(file);
        }

        await extractVehicleDocument(file, documentType);
    };

    const handleDocumentFileChange = async (event, documentType) => {
        const files = event.target.files;
        if (!files || !files[0]) return;
        await setDocumentFile(files[0], documentType);
        event.target.value = "";
    };

    const extractFacture = (event) => handleDocumentFileChange(event, "FACT");
    const extractCG = (event) => handleDocumentFileChange(event, "CG");
    const extractCG1 = (event) => handleDocumentFileChange(event, "CG1");
    const extractCG2 = (event) => handleDocumentFileChange(event, "CG2");

    const handleDropOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDocumentDrop = async (event, documentType) => {
        handleDropOver(event);
        if (isParsingDocument) return;

        const file = event.dataTransfer?.files?.[0];
        await setDocumentFile(file, documentType);
    };

    const handlePhotosChange = (event) => {
        const selected = Array.from(event.target.files || []);
        setPhotos(selected);
    };

    const removeSelectedPhoto = (selectedIndex) => {
        setPhotos(prev => prev.filter((_, index) => index !== selectedIndex));
    };

    const removeSavedDocument = async (doc) => {
        if (!doc?.id || deletingDocumentId) return;
        if (!window.confirm("Supprimer ce document ?")) return;

        setDeletingDocumentId(doc.id);
        try {
            await deleteDocument(doc.id);
            setForm(prev => ({
                ...prev,
                documents: (prev.documents || []).filter(item => item.id !== doc.id),
            }));
        } catch (e) {
            setRecordError({
                open: true,
                message: e?.response?.data?.message || "Erreur lors de la suppression du document.",
            });
        } finally {
            setDeletingDocumentId(null);
        }
    };

    const handlePhotosDrop = (event) => {
        handleDropOver(event);
        if (isParsingDocument) return;

        const selected = Array.from(event.dataTransfer?.files || []).filter(file => file.type?.startsWith("image/"));
        if (selected.length > 0) {
            setPhotos(selected);
        }
    };

    const activateDropInput = (ref) => {
        if (!isParsingDocument) {
            ref.current?.click();
        }
    };

    const handleDropInputKeyDown = (event, ref) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activateDropInput(ref);
        }
    };

    const renderExtractedDocumentCards = (documentTypes) => {
        const entries = Object.entries(extractedDocuments).filter(([documentType]) => documentTypes.includes(documentType));
        if (entries.length === 0) return null;

        return (
            <Row className="mt-4">
                {entries.map(([documentType, data]) => {
                    const fields = getExtractedFields(data, documentType);
                    return (
                        <Col md="12" key={documentType} className="mb-3">
                            <Card className="border shadow-sm">
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <i className="ri-scan-2-line me-2"></i>
                                        {t('vehiculef.extractedData')} - {documentLabelKeys[documentType] ? t(documentLabelKeys[documentType]) : documentType}
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button
                                            size="sm"
                                            type="button"
                                            color="primary"
                                            disabled={fields.length === 0 || isParsingDocument}
                                            onClick={() => applyExtractedData(data, documentType, true)}
                                        >
                                            {t('vehiculef.applyAll')}
                                        </Button>
                                        <Button
                                            size="sm"
                                            type="button"
                                            color="secondary"
                                            disabled={isParsingDocument}
                                            onClick={() => setExtractedDocuments(prev => {
                                                const next = { ...prev };
                                                delete next[documentType];
                                                return next;
                                            })}
                                        >
                                            {t('vehiculef.hide')}
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardBody>
                                    {Array.isArray(data?.ocrWarnings) && data.ocrWarnings.length > 0 && (
                                        <UncontrolledAlert color="warning">
                                            <div className="fw-semibold mb-1">{t('vehiculef.qualityTitle')}</div>
                                            <div className="mb-2">
                                                {t('vehiculef.qualityBody')}
                                            </div>
                                            <ul className="mb-0 ps-3">
                                                {data.ocrWarnings.map((warning, index) => (
                                                    <li key={index}>{warning}</li>
                                                ))}
                                            </ul>
                                        </UncontrolledAlert>
                                    )}
                                    {fields.length === 0 ? (
                                        <UncontrolledAlert color="warning" className="mb-0">
                                            {t('vehiculef.noExtractedData')}
                                        </UncontrolledAlert>
                                    ) : (
                                        <Table responsive bordered size="sm" className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>{t('vehiculef.field')}</th>
                                                    <th>{t('vehiculef.detectedValue')}</th>
                                                    <th style={{ width: 120 }}>{t('action')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fields.map(({ field, label, value }) => (
                                                    <tr key={field}>
                                                        <td>{label}</td>
                                                        <td>
                                                            <input
                                                                type={typeof value === "number" ? "number" : "text"}
                                                                inputMode={dateFields.has(field) ? "numeric" : undefined}
                                                                placeholder={dateFields.has(field) ? "d/MM/yyyy" : undefined}
                                                                className="form-control form-control-sm"
                                                                value={normalizeDateFieldValue(field, value)}
                                                                disabled={isParsingDocument}
                                                                onChange={(event) => updateExtractedField(documentType, field, event.target.value)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Button
                                                                size="sm"
                                                                type="button"
                                                                color="info"
                                                                disabled={isParsingDocument}
                                                                onClick={() => formik.setFieldValue(field, normalizeDateFieldValue(field, data[field]), false)}
                                                            >
                                                                {t('vehiculef.apply')}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        );
    };

    const calculateAge = (firstMec, dateSinistre) => {
        if (!firstMec) return { years: 0, months: 0 };

        const start = parseFormattedDate(firstMec);
        const end = dateSinistre ? parseFormattedDate(dateSinistre) : new Date();

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            return { years: 0, months: 0 };
        }

        let years = end.getFullYear() - start.getFullYear();
        let months = end.getMonth() - start.getMonth();

        if (months < 0) {
            years--;
            months += 12;
        }

        // If day of month not reached, adjust month difference
        if (end.getDate() < start.getDate()) {
            months--;
            if (months < 0) {
                years--;
                months += 12;
            }
        }

        return { years: Math.max(years, 0), months: Math.max(months, 0) };
    };


    const handleChangeCategVH = ({ target }) => {
        formik.setFieldValue('categVeh', categVH.find(d => d.code === target.value)?.code || '');
    };

    const renderPhotoEditorGrid = (photoList, compact = false) => {
        if (photoList.length === 0) return null;

        return (
            <div className={`vehicle-photo-editor-grid ${compact ? "is-compact" : ""}`}>
                {photoList.map((photo, index) => (
                    <div className="vehicle-photo-editor-tile" key={photo.id}>
                        <button
                            type="button"
                            className="vehicle-photo-editor-preview"
                            onClick={() => openPhotoViewer(photoList, index)}
                            aria-label={`Voir ${photo.title}`}
                        >
                            <img src={photo.url} alt={photo.title} />
                            <span className="vehicle-photo-editor-overlay">
                                <i className="ri-eye-line"></i>
                            </span>
                        </button>
                        <div className="vehicle-photo-editor-meta">
                            <span className="vehicle-photo-editor-name" title={photo.title}>{photo.title}</span>
                            <span className={`vehicle-photo-editor-badge ${photo.source === "selected" ? "is-new" : ""}`}>
                                {photo.source === "selected" ? "Nouvelle" : "Enregistree"}
                            </span>
                        </div>
                        {photo.source === "selected" && (
                            <button
                                type="button"
                                className="vehicle-photo-editor-remove"
                                onClick={() => removeSelectedPhoto(photo.selectedIndex)}
                                disabled={isParsingDocument}
                                aria-label={`Retirer ${photo.title}`}
                            >
                                <i className="ri-close-line"></i>
                            </button>
                        )}
                        {photo.source === "saved" && photo.document?.id && (
                            <button
                                type="button"
                                className="vehicle-photo-editor-remove"
                                onClick={() => removeSavedDocument(photo.document)}
                                disabled={deletingDocumentId === photo.document.id}
                                aria-label={`Supprimer ${photo.title}`}
                            >
                                <i className={deletingDocumentId === photo.document.id ? "ri-loader-4-line" : "ri-close-line"}></i>
                            </button>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <React.Fragment>
            {isFetching ? (
                <div id="preloader">
                    <div id="status">
                        <div className="spinner">
                            <i className="ri-loader-line spin-icon"></i>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="page-content">
                    <Container fluid={true}>
                        <Card className="page-compact-header mb-3 border-0 shadow-sm">
                            <CardBody>
                                <Row className="align-items-center g-3">
                                    <Col>
                                        <div className="d-flex align-items-center gap-3">
                                            <Button type="button" color="light" onClick={() => navigate(DATABASE_VEHICULE_PAGE)}>
                                                <i className="ri-arrow-left-line me-1"></i>
                                                {t('actions.back')}
                                            </Button>
                                            <div>
                                                <div className="page-inline-breadcrumb mb-2">
                                                    <span>{t("database")}</span>
                                                    <i className="ri-arrow-right-s-line"></i>
                                                    <strong>{t("vehicule")}</strong>
                                                </div>
                                                <h4 className="mb-0">{id > 0 ? t('editvehicule') : t('newvehicule')}</h4>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
		                                                
                            </CardBody>
                        </Card>
                        <Card>
                            <CardBody>
                                <AvForm
                                    className="needs-validation"
                                    onSubmit={(event) => {
                                        event?.preventDefault();
                                        requestSave();
                                    }}
                                >
                                    <div className="d-flex flex-wrap gap-2 mb-4">
                                        {formSteps.map((step, index) => (
                                            <Button
                                                key={step.key}
                                                type="button"
                                                color={step.key === currentStep ? "primary" : "light"}
                                                className="d-flex align-items-center gap-2"
                                                onClick={() => goToStep(step.key)}
                                            >
                                                <span className="fw-semibold">{index + 1}</span>
                                                <i className={step.icon}></i>
                                                <span>{t(step.labelKey)}</span>
                                            </Button>
                                        ))}
                                    </div>
                                    {/* Documents upload */}
                                    <Card className={`mt-4 ${!["carteGrise", "facture", "photos"].includes(currentStep) ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-car-line me-2"></i>
                                                {currentStep === "facture"
                                                    ? t('vehiculef.section_facture')
                                                    : currentStep === "photos"
                                                        ? t('vehiculef.section_photos')
                                                        : t('vehiculef.section_carte_grise')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={["carteGrise", "facture", "photos"].includes(currentStep)}>
                                            <CardBody>
                                                <UncontrolledAlert color="info">
                                                    {t('vehiculef.extractionHint')}
                                                </UncontrolledAlert>
                                                <Row>
                                                    <Col md="4" className={currentStep !== "carteGrise" ? "d-none" : ""}>
                                                        <Label className="form-label" htmlFor="cgFullFile">{t('vehiculef.cgFull')}</Label>
                                                        <div
                                                            className={`vehicle-drop-zone ${isParsingDocument ? "is-disabled" : ""}`}
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => activateDropInput(cgFullInputRef)}
                                                            onKeyDown={(event) => handleDropInputKeyDown(event, cgFullInputRef)}
                                                            onDragOver={handleDropOver}
                                                            onDragEnter={handleDropOver}
                                                            onDrop={(event) => handleDocumentDrop(event, "CG")}
                                                        >
                                                            <input
                                                                id="cgFullFile"
                                                                accept="application/pdf, image/jpeg, image/png, image/gif, image/bmp"
                                                                type="file"
                                                                className="vehicle-drop-input"
                                                                ref={cgFullInputRef}
                                                                onChange={extractCG}
                                                                disabled={isParsingDocument}
                                                            />
                                                            <i className="ri-upload-cloud-2-line"></i>
                                                            <strong>{cgFull?.name || t('vehiculef.dropDocument')}</strong>
                                                            <span>{t('vehiculef.clickToBrowse')}</span>
                                                        </div>
                                                        <div className="input-group mt-3">
                                                            {cgFull && <small>{cgFull.name}</small>}
                                                            {extractingDocument === "CG" && (
                                                                <small className="ms-2 text-primary d-flex align-items-center gap-1">
                                                                    <Spinner size="sm" /> {t('vehiculef.extracting')}
                                                                </small>
                                                            )}
                                                            {cgFull && (
                                                                <Button
                                                                    className="mx-2"
                                                                    size="sm"
                                                                    type="button"
                                                                    color="danger"
                                                                    disabled={isParsingDocument}
                                                                    onClick={() => clearDocumentFile("CG")}
                                                                >
                                                                    <i className="ri-delete-bin-2-line"></i>
                                                                </Button>
                                                            )}
                                                            {cgFull && (
                                                                <Button
                                                                    size="sm"
                                                                    type="button"
                                                                    color="info"
                                                                    disabled={isParsingDocument}
                                                                    onClick={() => {
                                                                        const url = window.URL.createObjectURL(cgFull);
                                                                        if (cgFull?.type === "application/pdf") {
                                                                            window.open(url, '_blank')?.focus();
                                                                        } else {
                                                                            const link = document.createElement("a");
                                                                            link.href = url;
                                                                            link.setAttribute("download", `${cgFull.name}`);
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                        }
                                                                    }}
                                                                >
                                                                    <i className="ri-file-download-line"></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </Col>
                                                    <Col md="4" className={currentStep !== "carteGrise" ? "d-none" : ""}>
                                                        <Label className="form-label" htmlFor="cgFile">{t('vehiculef.cg')}</Label>
                                                        <div
                                                            className={`vehicle-drop-zone ${isParsingDocument ? "is-disabled" : ""}`}
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => activateDropInput(cgInputRef)}
                                                            onKeyDown={(event) => handleDropInputKeyDown(event, cgInputRef)}
                                                            onDragOver={handleDropOver}
                                                            onDragEnter={handleDropOver}
                                                            onDrop={(event) => handleDocumentDrop(event, "CG1")}
                                                        >
                                                            <input
                                                                id="cgFile"
                                                                accept="application/pdf, image/jpeg, image/png, image/gif, image/bmp"
                                                                type="file"
                                                                className="vehicle-drop-input"
                                                                ref={cgInputRef}
                                                                onChange={extractCG1}
                                                                disabled={isParsingDocument}
                                                            />
                                                            <i className="ri-upload-cloud-2-line"></i>
                                                            <strong>{cg?.name || t('vehiculef.dropDocument')}</strong>
                                                            <span>{t('vehiculef.clickToBrowse')}</span>
                                                        </div>
                                                        <div className="input-group mt-3">
                                                            {cg && <small>{cg.name}</small>}
                                                            {extractingDocument === "CG1" && (
                                                                <small className="ms-2 text-primary d-flex align-items-center gap-1">
                                                                    <Spinner size="sm" /> {t('vehiculef.extracting')}
                                                                </small>
                                                            )}
                                                            {cg && (
                                                                <Button
                                                                    className="mx-2"
                                                                    size="sm"
                                                                    type="button"
                                                                    color="danger"
                                                                    disabled={isParsingDocument}
                                                                    onClick={() => clearDocumentFile("CG1")}
                                                                >
                                                                    <i className="ri-delete-bin-2-line"></i>
                                                                </Button>
                                                            )}
                                                            {cg && (
                                                                <Button
                                                                    size="sm"
                                                                    type="button"
                                                                    color="info"
                                                                    disabled={isParsingDocument}
                                                                    onClick={() => {
                                                                        const url = window.URL.createObjectURL(cg);
                                                                        if (cg?.type === "application/pdf") {
                                                                            window.open(url, '_blank')?.focus();
                                                                        } else {
                                                                            const link = document.createElement("a");
                                                                            link.href = url;
                                                                            link.setAttribute("download", `${cg.name}`);
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                        }
                                                                    }}
                                                                >
                                                                    <i className="ri-file-download-line"></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </Col>
                                                    <Col md="4" className={currentStep !== "carteGrise" ? "d-none" : ""}>
                                                        <Label className="form-label" htmlFor="cg2File">{t('vehiculef.cg2')}</Label>
                                                        <div
                                                            className={`vehicle-drop-zone ${isParsingDocument ? "is-disabled" : ""}`}
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => activateDropInput(cg2InputRef)}
                                                            onKeyDown={(event) => handleDropInputKeyDown(event, cg2InputRef)}
                                                            onDragOver={handleDropOver}
                                                            onDragEnter={handleDropOver}
                                                            onDrop={(event) => handleDocumentDrop(event, "CG2")}
                                                        >
                                                            <input
                                                                id="cg2File"
                                                                accept="application/pdf, image/jpeg, image/png, image/gif, image/bmp"
                                                                type="file"
                                                                className="vehicle-drop-input"
                                                                ref={cg2InputRef}
                                                                onChange={extractCG2}
                                                                disabled={isParsingDocument}
                                                            />
                                                            <i className="ri-upload-cloud-2-line"></i>
                                                            <strong>{cg2?.name || t('vehiculef.dropDocument')}</strong>
                                                            <span>{t('vehiculef.clickToBrowse')}</span>
                                                        </div>
                                                        <div className="input-group mt-3">
                                                            {cg2 && <small>{cg2.name}</small>}
                                                            {extractingDocument === "CG2" && (
                                                                <small className="ms-2 text-primary d-flex align-items-center gap-1">
                                                                    <Spinner size="sm" /> {t('vehiculef.extracting')}
                                                                </small>
                                                            )}
                                                            {cg2 && (
                                                                <Button
                                                                    className="mx-2"
                                                                    size="sm"
                                                                    type="button"
                                                                    color="danger"
                                                                    disabled={isParsingDocument}
                                                                    onClick={() => clearDocumentFile("CG2")}
                                                                >
                                                                    <i className="ri-delete-bin-2-line"></i>
                                                                </Button>
                                                            )}
                                                            {cg2 && (
                                                                <Button
                                                                    size="sm"
                                                                    type="button"
                                                                    color="info"
                                                                    disabled={isParsingDocument}
                                                                    onClick={() => {
                                                                        const url = window.URL.createObjectURL(cg2);
                                                                        if (cg2?.type === "application/pdf") {
                                                                            window.open(url, '_blank')?.focus();
                                                                        } else {
                                                                            const link = document.createElement("a");
                                                                            link.href = url;
                                                                            link.setAttribute("download", `${cg2.name}`);
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                        }
                                                                    }}
                                                                >
                                                                    <i className="ri-file-download-line"></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </Col>

                                                    <Col md="6" className={currentStep !== "facture" ? "d-none" : ""}>
                                                        <Label className="form-label" htmlFor="factureFile">{t('vehiculef.facture')}</Label>
                                                        <div
                                                            className={`vehicle-drop-zone ${isParsingDocument ? "is-disabled" : ""}`}
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => activateDropInput(factureInputRef)}
                                                            onKeyDown={(event) => handleDropInputKeyDown(event, factureInputRef)}
                                                            onDragOver={handleDropOver}
                                                            onDragEnter={handleDropOver}
                                                            onDrop={(event) => handleDocumentDrop(event, "FACT")}
                                                        >
                                                            <input
                                                                id="factureFile"
                                                                accept="application/pdf, image/jpeg, image/png, image/gif, image/bmp"
                                                                type="file"
                                                                className="vehicle-drop-input"
                                                                ref={factureInputRef}
                                                                onChange={extractFacture}
                                                                disabled={isParsingDocument}
                                                            />
                                                            <i className="ri-upload-cloud-2-line"></i>
                                                            <strong>{facture?.name || t('vehiculef.dropDocument')}</strong>
                                                            <span>{t('vehiculef.clickToBrowse')}</span>
                                                        </div>
                                                        <div className="input-group mt-3">
                                                            {facture && <small>{facture.name}</small>}
                                                            {extractingDocument === "FACT" && (
                                                                <small className="ms-2 text-primary d-flex align-items-center gap-1">
                                                                    <Spinner size="sm" /> {t('vehiculef.extracting')}
                                                                </small>
                                                            )}
                                                            {facture && (
                                                                <Button
                                                                    className="mx-2"
                                                                    size="sm"
                                                                    type="button"
                                                                    color="danger"
                                                                    disabled={isParsingDocument}
                                                                    onClick={() => clearDocumentFile("FACT")}
                                                                >
                                                                    <i className="ri-delete-bin-2-line"></i>
                                                                </Button>
                                                            )}
                                                            {facture && (
                                                                <Button
                                                                    size="sm"
                                                                    type="button"
                                                                    color="info"
                                                                    disabled={isParsingDocument}
                                                                    onClick={() => {
                                                                        const url = window.URL.createObjectURL(facture);
                                                                        if (facture?.type === "application/pdf") {
                                                                            window.open(url, '_blank')?.focus();
                                                                        } else {
                                                                            const link = document.createElement("a");
                                                                            link.href = url;
                                                                            link.setAttribute("download", `${facture.name}`);
                                                                            document.body.appendChild(link);
                                                                            link.click();
                                                                        }
                                                                    }}
                                                                >
                                                                    <i className="ri-file-download-line"></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </Col>
                                                    <Col md="12" className={currentStep !== "photos" ? "d-none" : ""}>
                                                        <div className="vehicle-photo-manager">
                                                            <div className="vehicle-photo-manager-header">
                                                                <div>
                                                                    <Label className="form-label mb-1" htmlFor="photoFiles">{t('vehiculef.photos')}</Label>
                                                                    <div className="text-muted small">
                                                                        {existingPhotoPreviews.length} enregistrees - {photos.length} nouvelles
                                                                    </div>
                                                                </div>
                                                                {photos.length > 0 && (
                                                                    <Button
                                                                        size="sm"
                                                                        type="button"
                                                                        color="light"
                                                                        disabled={isParsingDocument}
                                                                        onClick={() => clearDocumentFile("PHOTO")}
                                                                    >
                                                                        <i className="ri-delete-bin-2-line me-1"></i>
                                                                        Retirer nouvelles
                                                                    </Button>
                                                                )}
                                                            </div>
                                                            <div
                                                                className={`vehicle-photo-drop-panel ${isParsingDocument ? "is-disabled" : ""}`}
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => activateDropInput(photosInputRef)}
                                                                onKeyDown={(event) => handleDropInputKeyDown(event, photosInputRef)}
                                                                onDragOver={handleDropOver}
                                                                onDragEnter={handleDropOver}
                                                                onDrop={handlePhotosDrop}
                                                            >
                                                                <input
                                                                    id="photoFiles"
                                                                    accept="image/jpeg, image/png, image/gif, image/bmp, image/webp"
                                                                    type="file"
                                                                    multiple
                                                                    className="vehicle-drop-input"
                                                                    ref={photosInputRef}
                                                                    onChange={handlePhotosChange}
                                                                    disabled={isParsingDocument}
                                                                />
                                                                <span className="vehicle-photo-drop-icon"><i className="ri-image-add-line"></i></span>
                                                                <span>
                                                                    <strong>{photos.length > 0 ? `${photos.length} ${t('vehiculef.selectedPhotos')}` : t('vehiculef.dropPhotos')}</strong>
                                                                    <small>{t('vehiculef.clickToBrowse')}</small>
                                                                </span>
                                                            </div>
                                                            {allPhotoPreviews.length > 0 ? (
                                                                renderPhotoEditorGrid(allPhotoPreviews)
                                                            ) : (
                                                                <div className="vehicle-photo-empty-state">
                                                                    <i className="ri-image-line"></i>
                                                                    <span>Aucune photo ajoutee.</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
		                                                
	                                                {currentStep === "carteGrise" && renderExtractedDocumentCards(["CG", "CG1", "CG2"])}
	                                                {currentStep === "facture" && renderExtractedDocumentCards(["FACT"])}

                                                    {currentStepFileDocuments.length > 0 && (
                                                        <Row className="mt-3">
                                                            {currentStepFileDocuments.map((doc) => (
                                                                <Col md="6" lg="4" key={doc.id || doc.filename} className="mb-3">
                                                                    <Card className="shadow-sm border h-100">
                                                                        <CardBody>
                                                                            <div className="d-flex align-items-center mb-3">
                                                                                {doc.fileType === "application/pdf" ? (
                                                                                    <i className="ri-file-pdf-line text-danger" style={{ fontSize: 30 }}></i>
                                                                                ) : (
                                                                                    <i className="ri-image-line text-info" style={{ fontSize: 30 }}></i>
                                                                                )}
                                                                                <div className="ms-2 min-w-0">
                                                                                    <h6 className="mb-0 text-truncate">{doc.filenameUser || doc.filename}</h6>
                                                                                    <small className="text-muted">{doc.createdDate}</small>
                                                                                </div>
                                                                            </div>

                                                                            <div className="d-flex justify-content-between gap-2">
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="info"
                                                                                    onClick={() => window.open(resolveDocumentUrl(doc), "_blank")}
                                                                                >
                                                                                    <i className="ri-eye-line me-1"></i>
                                                                                    {t('actions.view')}
                                                                                </Button>
                                                                                <Button
                                                                                    size="sm"
                                                                                    color="light"
                                                                                    className="text-danger"
                                                                                    disabled={deletingDocumentId === doc.id}
                                                                                    onClick={() => removeSavedDocument(doc)}
                                                                                >
                                                                                    <i className={deletingDocumentId === doc.id ? "ri-loader-4-line me-1" : "ri-delete-bin-2-line me-1"}></i>
                                                                                    Supprimer
                                                                                </Button>
                                                                            </div>
                                                                        </CardBody>
                                                                    </Card>
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    )}

	                                            </CardBody>
                                        </Collapse>
                                    </Card>
                                    {/* Basic vehicle fields */}


                                    {/* ===================== SECTION CHASSIS & IDENTIFICATION ===================== */}
                                    <Card className={`mt-4 ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-car-line me-2"></i>
                                                {t('vehiculef.section_chassis')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "details"}>
                                            <CardBody>
                                                <Row className='mt-3'>
                                                    <Col md="4">
                                                        <Label htmlFor="categorie" className="form-label">{t('parametrage.categorieV')}</Label>
                                                        <select
                                                            className="form-control"
                                                            id='categorie'
                                                            {...formik.getFieldProps('categVeh')}
                                                            onChange={handleChangeCategVH}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {categVH.map((option, index) =>
                                                                <option key={index} value={option.code}>
                                                                    {option.description}
                                                                </option>
                                                            )}
                                                        </select>
                                                        {error && <p style={{ color: "red" }}>{error}</p>}
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="marque">{t('vehiculef.marque')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('marque')}
                                                            placeholder={t('vehiculef.marque')}
                                                            type="text"
                                                            errorMessage={t('message.required')}
                                                            className="form-control"
                                                            id="marque"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="type">{t('vehiculef.type')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('type')}
                                                            placeholder={t('vehiculef.type')}
                                                            type="text"
                                                            errorMessage={t('message.required')}
                                                            className="form-control"
                                                            id="type"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="genre">{t('vehiculef.genre')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('genre')}
                                                            placeholder={t('vehiculef.genre')}
                                                            type="text"
                                                            className="form-control"
                                                            id="genre"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="modele">{t('vehiculef.modele')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('modele')}
                                                            placeholder={t('vehiculef.modele')}
                                                            type="text"
                                                            className="form-control"
                                                            id="modele"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="carburant">{t('vehiculef.carburant')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('carburant')}
                                                            placeholder={t('vehiculef.carburant')}
                                                            errorMessage={t('message.required')}
                                                            type="text"
                                                            className="form-control"
                                                            id="carburant"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="numChassis">{t('vehiculef.numChassis')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('numChassis')}
                                                            placeholder={t('vehiculef.numChassis')}
                                                            errorMessage={t('message.required')}
                                                            type="text"
                                                            className="form-control"
                                                            id="numChassis"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="nbrCylindres">{t('vehiculef.nbrCylindres')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('nbrCylindres')}
                                                            placeholder={t('vehiculef.nbrCylindres')}
                                                            errorMessage={t('message.required')}
                                                            type="number"
                                                            className="form-control"
                                                            id="nbrCylindres"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="puissFiscale">{t('vehiculef.puissFiscale')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('puissFiscale')}
                                                            placeholder={t('vehiculef.puissFiscale')}
                                                            errorMessage={t('message.required')}
                                                            type="number"
                                                            className="form-control"
                                                            id="puissFiscale"
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="immatriculation">{t('vehiculef.immatriculation')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('immatriculation')}
                                                            id="immatriculation"
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="immanterieur">{t('vehiculef.immanterieur')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('immanterieur')}
                                                            id="immanterieur"
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    </Col>

                                                    <Col md="3" className="d-none">
                                                        <Label className="form-label" htmlFor="firstMec">{t('vehiculef.firstMec')}</Label>
                                                        <AvField
                                                            name="firstMec"
                                                            id="firstMec"
                                                            type="text"
                                                            inputMode="numeric"
                                                            placeholder="d/MM/yyyy"
                                                            className="form-control"
                                                            value={formatDateForDisplay(formik.values.firstMec)}
                                                            onChange={(event) => handleDateFieldChange("firstMec", event.target.value)}
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="mecMaroc">{t('vehiculef.mecMaroc')}</Label>
                                                        <AvField
                                                            name="mecMaroc"
                                                            id="mecMaroc"
                                                            type="text"
                                                            inputMode="numeric"
                                                            placeholder="d/MM/yyyy"
                                                            className="form-control"
                                                            value={formatDateForDisplay(formik.values.mecMaroc)}
                                                            onChange={(event) => handleDateFieldChange("mecMaroc", event.target.value)}
                                                        />
                                                    </Col>

                                                    <Col md="3" className="d-none">
                                                        <Label className="form-label" htmlFor="ageMonths">{t('vehiculef.ageMonths')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('ageMonths')}
                                                            id="ageMonths"
                                                            type="number"
                                                            className="form-control"
                                                            disabled
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="ageYears">{t('vehiculef.ageYears')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('ageYears')}
                                                            id="ageYears"
                                                            type="number"
                                                            className="form-control"
                                                            disabled
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="compteur">{t('vehiculef.compteur')} (km)</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('compteur')}
                                                            id="compteur"
                                                            type="number"
                                                            className="form-control"
                                                        />
                                                    </Col>


                                                </Row>
                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    {/* ===================== SECTION SINISTRE ===================== */}
                                    <Card className={`mt-4 ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-alert-line me-2"></i>
                                                {t('vehiculef.section_sinistre')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "details"}>
                                            <CardBody>
                                                <Row>
                                                    <Col md="4">
                                                        <Label className="form-label" htmlFor="dateSinistre">{t('vehiculef.dateSinistre')}</Label>
                                                        <AvField
                                                            name="dateSinistre"
                                                            id="dateSinistre"
                                                            type="text"
                                                            inputMode="numeric"
                                                            placeholder="d/MM/yyyy"
                                                            className="form-control"
                                                            value={formatDateForDisplay(formik.values.dateSinistre)}
                                                            onChange={(event) => handleDateFieldChange("dateSinistre", event.target.value)}
                                                        />
                                                    </Col>
                                                    <Col md="4">
                                                        <Label className="form-label" htmlFor="etatVehicule">{t('vehiculef.etatVehicule')}</Label>
                                                        <select
                                                            className="form-control"
                                                            id='etatVehicule'
                                                            {...formik.getFieldProps('etatVehicule')}
                                                            onChange={(e) => formik.setFieldValue('etatVehicule', e.target.value)}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            <option value="ETAT_1">Bon</option>
                                                            <option value="ETAT_2">Moyen</option>
                                                        </select>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    {/* ===================== SECTION ADMINISTRATIF ===================== */}
                                    <Card className={`mt-4 ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-file-list-line me-2"></i>
                                                {t('vehiculef.section_admin')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "details"}>
                                            <CardBody>
                                                <Row>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="usage">{t('vehiculef.usage')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('usage')}
                                                            id="usage"
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="proprietere">{t('vehiculef.proprietere')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('proprietere')}
                                                            id="proprietere"
                                                            type="text"
                                                            className="form-control"
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="ville">{t('vehiculef.ville')}</Label>
                                                        <select
                                                            className="form-control"
                                                            id='ville'
                                                            {...formik.getFieldProps('ville')}
                                                            value={formik.values.ville?.id}
                                                            onChange={handleChangeVille}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {villes.map((option, index) =>
                                                                <option key={index} value={option.id}>
                                                                    {option.description}
                                                                </option>
                                                            )}
                                                        </select>
                                                        {error && <p style={{ color: "red" }}>{error}</p>}
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="assurance">{t('vehiculef.assurance')}</Label>

                                                        <select
                                                            className="form-control"
                                                            id='assurance'
                                                            {...formik.getFieldProps('assurance')}
                                                            value={formik.values.assurance?.id}
                                                            onChange={handleChangeAssurance}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {assurances.map((option, index) =>
                                                                <option key={index} value={option.id}>
                                                                    {option.description}
                                                                </option>
                                                            )}
                                                        </select>
                                                        {error && <p style={{ color: "red" }}>{error}</p>}
                                                    </Col>

                                                    <Col md="4">
                                                        <Label className="form-label" htmlFor="entretReg">{t('vehiculef.entretReg')}</Label>
                                                        <select
                                                            className="form-control"
                                                            id='prestataire'
                                                            {...formik.getFieldProps('prestataire')}
                                                            value={formik.values.prestataire?.id}
                                                            onChange={({ target }) => {
                                                                if (target.value) {
                                                                    formik.setFieldValue('prestataire', { id: target.value });
                                                                    const struc = entrestruc.find(e => e.id == target.value);
                                                                    formik.setFieldValue('entretReg', struc.entretReg);
                                                                }
                                                            }}
                                                        >
                                                            <option value="">Sélectionner...</option>
                                                            {entrestruc.map((option, index) =>
                                                                <option key={index} value={option.id}>
                                                                    {`${option.entretReg} - ${option.prestataire}`}
                                                                </option>
                                                            )}
                                                        </select>
                                                        {error && <p style={{ color: "red" }}>{error}</p>}
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    {/* ===================== SECTION VALEURS & FINANCE ===================== */}
                                    <Card className={`mt-4 mb-4 ${currentStep !== "details" ? "d-none" : ""}`}>
                                        <CardHeader
                                            className="d-flex justify-content-between align-items-center"
                                        >
                                            <div>
                                                <i className="ri-money-dollar-circle-line me-2"></i>
                                                {t('vehiculef.section_valeurs')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "details"}>
                                            <CardBody>
                                                <Row>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="prixAchat">{t('vehiculef.prixAchat')}</Label>
                                                        <AvField
                                                            {...formik.getFieldProps('prixAchat')}
                                                            placeholder={t('vehiculef.prixAchat')}
                                                            errorMessage={t('message.required')}
                                                            type="number"
                                                            className="form-control"
                                                            id="prixAchat"
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="valMarche">{t('vehiculef.valMarche')}</Label>
                                                        <CurrencyInput
                                                            id="valMarche"
                                                            name="valMarche"
                                                            className="form-control"
                                                            value={formik.values.valMarche}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("valMarche", v)}
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="epave">{t('vehiculef.epave')}</Label>
                                                        <CurrencyInput
                                                            id="epave"
                                                            name="epave"
                                                            className="form-control"
                                                            value={formik.values.epave}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("epave", v)}
                                                        />
                                                    </Col>

                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="devis">{t('vehiculef.devis')}</Label>
                                                        <CurrencyInput
                                                            id="devis"
                                                            name="devis"
                                                            className="form-control"
                                                            value={formik.values.devis}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("devis", v)}
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="fraisimm">{t('vehiculef.fraisimm')}</Label>
                                                        <CurrencyInput
                                                            id="fraisimm"
                                                            name="fraisimm"
                                                            className="form-control"
                                                            value={formik.values.fraisimm}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("fraisimm", v)}
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="remise">{t('vehiculef.remise')}</Label>
                                                        <CurrencyInput
                                                            id="remise"
                                                            name="remise"
                                                            className="form-control"
                                                            value={formik.values.remise}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("remise", v)}
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        <Label className="form-label" htmlFor="remise">{t('vehiculef.coefCommercial')}</Label>
                                                        <CurrencyInput
                                                            id="coefCommercial"
                                                            name="coefCommercial"
                                                            className="form-control"
                                                            value={formik.values.coefCommercial}
                                                            decimalsLimit={2}
                                                            onValueChange={(v) => formik.setFieldValue("coefCommercial", v)}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className="mt-4">
                                                    <Col md="12">
                                                        {reform?.map((item, index) => (
                                                            <FormGroup check inline key={index}>
                                                                <div className="mb-3 form-check">
                                                                    <AvField
                                                                        type="radio"
                                                                        name="reform"
                                                                        value={item.code}
                                                                        className="form-check-input"
                                                                        id={`reform_${index}`}
                                                                        checked={item.code === formik.values.reform}
                                                                        onChange={() => formik.setFieldValue('reform', item.code)}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`reform_${index}`}>
                                                                        {t(item.description)}
                                                                    </label>
                                                                </div>
                                                            </FormGroup>
                                                        ))}
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    <Card className={`mt-4 mb-4 ${currentStep !== "resume" ? "d-none" : ""}`}>
                                        <CardHeader className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <i className="ri-clipboard-line me-2"></i>
                                                {t('vehiculef.section_resume')}
                                            </div>
                                        </CardHeader>
                                        <Collapse isOpen={currentStep === "resume"}>
                                            <CardBody>
                                                <Row>
                                                    {[
                                                        "marque",
                                                        "modele",
                                                        "type",
                                                        "immatriculation",
                                                        "numChassis",
                                                        "firstMec",
                                                        "compteur",
                                                        "prixAchat",
                                                        "remise",
                                                        "fraisimm",
                                                        "valMarche",
                                                        "ville",
                                                        "assurance",
                                                    ].map((field) => {
                                                        const rawValue = formik.values[field];
                                                        const value = rawValue && typeof rawValue === "object"
                                                            ? rawValue.description || rawValue.libelle || rawValue.id
                                                            : rawValue;

                                                        return (
                                                            <Col md="4" key={field} className="mb-3">
                                                                <div className="text-muted small">{t(extractedFieldLabelKeys[field] || `vehiculef.${field}`)}</div>
                                                                <div className="fw-semibold">{value || "-"}</div>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                                <Row className="mt-3">
                                                    <Col md="4" className="mb-3">
                                                        <div className="text-muted small">{t('vehiculef.cgFull')}</div>
                                                        <div className="fw-semibold">{cgFull?.name || cg?.name || "-"}</div>
                                                    </Col>
                                                    <Col md="4" className="mb-3">
                                                        <div className="text-muted small">{t('vehiculef.facture')}</div>
                                                        <div className="fw-semibold">{facture?.name || "-"}</div>
                                                    </Col>
                                                    <Col md="4" className="mb-3">
                                                        <div className="text-muted small">{t('vehiculef.photos')}</div>
                                                        <div className="fw-semibold">{allPhotoPreviews.length} {t('vehiculef.selectedPhotos')}</div>
                                                    </Col>
	                                                </Row>

                                                    <h6 className="mt-4 mb-3">{t('vehiculef.documents')}</h6>
                                                    {resumeDocuments.length > 0 ? (
                                                        <Table responsive size="sm" className="align-middle">
                                                            <thead>
                                                                <tr>
                                                                    <th>Type</th>
                                                                    <th>Fichier</th>
                                                                    <th>Statut</th>
                                                                    <th className="text-end">Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {resumeDocuments.map((doc) => (
                                                                    <tr key={doc.id}>
                                                                        <td>{doc.label}</td>
                                                                        <td>{doc.name}</td>
                                                                        <td>
                                                                            <span className={`vehicle-photo-editor-badge ${doc.source === "selected" ? "is-new" : ""}`}>
                                                                                {doc.source === "selected" ? "Nouvelle" : "Enregistree"}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <Button size="sm" color="light" onClick={doc.open}>
                                                                                <i className="ri-eye-line me-1"></i>
                                                                                {t('actions.view')}
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    ) : (
                                                        <p className="text-muted mb-0">Aucun document attache.</p>
                                                    )}

                                                    {allPhotoPreviews.length > 0 && <h6 className="mt-4 mb-3">{t('vehiculef.photos')}</h6>}
	                                                {renderPhotoEditorGrid(allPhotoPreviews, true)}
	                                            </CardBody>
                                        </Collapse>
                                    </Card>

                                    <div className="d-flex justify-content-between align-items-center mt-4">
                                        <Button
                                            type="button"
                                            color="light"
                                            disabled={isFirstStep || isParsingDocument}
                                            onClick={goToPreviousStep}
                                        >
                                            <i className="ri-arrow-left-line me-1"></i>
                                            {t('actions.previous')}
                                        </Button>
                                        <div className="d-flex gap-2">
                                            <Button type="button" color="secondary" onClick={() => navigate(DATABASE_VEHICULE_PAGE)}>
                                                {t('actions.cancel')}
                                            </Button>
                                            {!isLastStep && (
                                                <Button type="button" color="info" disabled={isParsingDocument} onClick={goToNextStep}>
                                                    {t('actions.next')}
                                                    <i className="ri-arrow-right-line ms-1"></i>
                                                </Button>
                                            )}
                                            <Button color="primary" type="button" disabled={isParsingDocument} onClick={requestSave}>
                                                {t('actions.save')}
                                            </Button>
                                        </div>
                                    </div>
                                </AvForm>
                            </CardBody>
                        </Card>
                    </Container>
                </div>
            )}

            <Modal
                isOpen={recordError.open}
                toggle={() => setRecordError({ ...recordError, open: false })}
                backdrop="static"
            >
                <ModalHeader toggle={() => setRecordError({ ...recordError, open: false })}>
                    {t('text.errorTitle')}
                </ModalHeader>
                <ModalBody>
                    <p>{recordError.message}</p>
                    <ModalFooter>
                        <Button
                            type="button"
                            color="light"
                            onClick={() => setRecordError({ ...recordError, open: false })}
                        >
                            {t('actions.close')}
                        </Button>
                    </ModalFooter>
                </ModalBody>
            </Modal>

            <Modal
                isOpen={saveConfirm}
                toggle={() => setSaveConfirm(false)}
                backdrop="static"
            >
                <ModalHeader toggle={() => setSaveConfirm(false)}>
                    Confirmer l'enregistrement
                </ModalHeader>
                <ModalBody>
                    <p className="mb-0">
                        Vous pouvez enregistrer ce vehicule meme si certaines informations ne sont pas encore renseignees.
                        Confirmez-vous l'enregistrement ?
                    </p>
                    <ModalFooter>
                        <Button type="button" color="light" onClick={() => setSaveConfirm(false)}>
                            Annuler
                        </Button>
                        <Button type="button" color="primary" onClick={confirmSave}>
                            Enregistrer
                        </Button>
                    </ModalFooter>
                </ModalBody>
            </Modal>

            {photoViewer.open && photoViewer.photos.length > 0 && (
                <Lightbox
                    mainSrc={photoViewer.photos[photoViewer.index].url}
                    nextSrc={photoViewer.photos[(photoViewer.index + 1) % photoViewer.photos.length]?.url}
                    prevSrc={photoViewer.photos[(photoViewer.index + photoViewer.photos.length - 1) % photoViewer.photos.length]?.url}
                    imageTitle={photoViewer.photos[photoViewer.index].title}
                    onCloseRequest={() => setPhotoViewer(prev => ({ ...prev, open: false }))}
                    onMovePrevRequest={() => setPhotoViewer(prev => ({
                        ...prev,
                        index: (prev.index + prev.photos.length - 1) % prev.photos.length,
                    }))}
                    onMoveNextRequest={() => setPhotoViewer(prev => ({
                        ...prev,
                        index: (prev.index + 1) % prev.photos.length,
                    }))}
                    enableZoom={true}
                />
            )}
        </React.Fragment>
    );
};

export default VehiculeForm;
